import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom } from 'rxjs';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompanyDirectoryService } from '@shared/services/company-directory.service';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { WorkCycleConfig } from '../work-cycle/work-cycle.interface';
import {
  RestConfigurationConfig,
  RestConfigurationCreatePayload,
  RestConfigurationFormValue,
  RestConfigurationUpdatePayload,
} from './rest-configuration.interface';

type RestConfigurationMode = 'view' | 'create' | 'edit';

interface CompaniesRecordsResponse {
  items?: CompaniesRecord[];
  data?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

@Component({
  selector: 'app-rest-configuration-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    LoadingStateComponent,
    CanAccessDirective,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslateModule,
  ],
  templateUrl: './rest-configuration-detail.component.html',
  styleUrl: './rest-configuration-detail.component.scss',
})
export class RestConfigurationDetailComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly companyDirectory = inject(CompanyDirectoryService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  mode: RestConfigurationMode = 'view';
  selectedConfigurationId: string | null = null;
  selectedConfiguration: RestConfigurationConfig | null = null;
  configurations: RestConfigurationConfig[] = [];
  companies: CompaniesRecord[] = [];
  workCycles: WorkCycleConfig[] = [];
  selectedCompanyId = '';
  form: RestConfigurationFormValue = this.createEmptyForm();

  readonly readAccess = { resource: 'restConfigurations', action: 'read' } as const;
  readonly createAccess = { resource: 'restConfigurations', action: 'create' } as const;
  readonly updateAccess = { resource: 'restConfigurations', action: 'update' } as const;
  readonly deleteAccess = { resource: 'restConfigurations', action: 'delete' } as const;
  readonly restConfigurationModes = ['fixed_weekday', 'cycle_reference'] as const;
  readonly weekdayOptions = [1, 2, 3, 4, 5, 6, 7] as const;

  ngOnInit(): void {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.configurations = this.configurations.map((item) => this.normalizeConfiguration(item));
        if (this.selectedConfiguration) {
          this.selectedConfiguration = this.normalizeConfiguration(this.selectedConfiguration);
        }
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    void this.loadCompanies(false).then(() => this.loadWorkCycles(false)).then(() => this.syncWithRoute());
  }

  get hasMultipleCompanies(): boolean {
    return this.companies.length > 1;
  }

  get canCreate(): boolean {
    return this.hasAccess('create');
  }

  get canUpdate(): boolean {
    return this.hasAccess('update');
  }

  get canDelete(): boolean {
    return this.hasAccess('delete');
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('restConfigurationsCatalog.detail.createTitle');
    }
    if (this.isEditMode) {
      return this.t('restConfigurationsCatalog.detail.editTitle');
    }
    return this.selectedConfiguration?.name || this.t('restConfigurationsCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('restConfigurationsCatalog.detail.createDescription');
    }
    if (this.isEditMode) {
      return this.t('restConfigurationsCatalog.detail.editDescription');
    }
    return this.t('restConfigurationsCatalog.detail.description');
  }

  get availableWorkCycles(): WorkCycleConfig[] {
    if (!this.form.companiesRecordId) {
      return [];
    }
    return this.workCycles
      .filter((cycle) => cycle.companiesRecordId === this.form.companiesRecordId)
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  get availableRestDayOptions(): number[] {
    const selectedCycle = this.availableWorkCycles.find((cycle) => cycle.workCycleId === this.form.cycleReference);
    const cycleLength = Number(selectedCycle?.cycleLength || 0);
    if (!cycleLength) {
      return [];
    }
    return Array.from({ length: cycleLength }, (_, index) => index + 1);
  }

  get isFixedWeekdayMode(): boolean {
    return this.form.mode === 'fixed_weekday';
  }

  get isCycleReferenceMode(): boolean {
    return this.form.mode === 'cycle_reference';
  }

  async refresh(): Promise<void> {
    await this.syncWithRoute();
  }

  async onCompanyChange(): Promise<void> {
    this.selectedCompanyId = this.form.companiesRecordId;
    this.reconcileCycleReference();
  }

  onModeChange(): void {
    if (this.isFixedWeekdayMode) {
      this.form.restDays = [];
      this.form.cycleReference = '';
      return;
    }

    if (this.isCycleReferenceMode) {
      this.form.weekdays = [];
      this.reconcileCycleReference();
    }
  }

  startCreate(): void {
    this.enterCreateMode(true);
  }

  startEdit(): void {
    this.enterEditMode(true);
  }

  cancelEdit(): void {
    this.exitEditMode(true);
  }

  goBackToList(): void {
    void this.router.navigate(['/attendance/rest-configurations']);
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isCreateMode) {
        const payload = this.buildCreatePayload();
        const created = await firstValueFrom(
          this.apiService.post<RestConfigurationConfig>('labor-config/rest-configurations', payload)
        );
        const createdId = created?.restConfigurationId || null;
        await this.loadConfigurations(createdId, false);
        this.showNotification(
          'snackbar-success',
          this.t('restConfigurationsCatalog.notifications.created'),
          'bottom',
          'center'
        );
        if (createdId) {
          void this.router.navigate(['/attendance/rest-configurations', createdId]);
        }
      } else if (this.isEditMode && this.selectedConfigurationId) {
        const payload = this.buildUpdatePayload();
        await firstValueFrom(
          this.apiService.patch<RestConfigurationConfig>(
            `labor-config/rest-configurations/${this.selectedConfigurationId}`,
            payload
          )
        );
        await this.loadConfigurations(this.selectedConfigurationId, false);
        this.showNotification(
          'snackbar-success',
          this.t('restConfigurationsCatalog.notifications.updated'),
          'bottom',
          'center'
        );
        void this.router.navigate(['/attendance/rest-configurations', this.selectedConfigurationId]);
      }
    } catch (error) {
      console.error('Error saving rest configuration', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('restConfigurationsCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedConfiguration || !this.canDelete) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('restConfigurationsCatalog.dialog.deleteTitle'),
        message: this.t('restConfigurationsCatalog.dialog.deleteMessage', {
          config: this.selectedConfiguration.name,
        }),
        confirmLabel: this.t('restConfigurationsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedConfigurationId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(
          this.apiService.delete(`labor-config/rest-configurations/${this.selectedConfigurationId}`)
        );
        await this.loadConfigurations(null, false);
        this.showNotification(
          'snackbar-success',
          this.t('restConfigurationsCatalog.notifications.deleted'),
          'bottom',
          'center'
        );
        void this.router.navigate(['/attendance/rest-configurations']);
      } catch (error) {
        console.error('Error deleting rest configuration', error);
        this.showNotification(
          'snackbar-danger',
          this.getApiErrorMessage(error, this.t('restConfigurationsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  getModeLabel(value: string | null | undefined): string {
    const normalized = this.normalizeOptionalText(value);
    if (!normalized) {
      return '-';
    }
    if (this.restConfigurationModes.includes(normalized as (typeof this.restConfigurationModes)[number])) {
      return this.t(`restConfigurationsCatalog.modeOptions.${normalized}`);
    }
    return normalized;
  }

  getWeekdaysLabel(values: number[] | null | undefined): string {
    if (!values?.length) {
      return this.t('restConfigurationsCatalog.labels.noWeekdays');
    }
    return values
      .map((weekday) => this.t(`restConfigurationsCatalog.weekdayOptions.${weekday}`))
      .join(', ');
  }

  getRestDaysLabel(values: number[] | null | undefined): string {
    if (!values?.length) {
      return this.t('restConfigurationsCatalog.labels.noRestDays');
    }
    return values.join(', ');
  }

  getCycleName(workCycleId: string | null | undefined): string {
    if (!workCycleId) {
      return this.t('restConfigurationsCatalog.labels.noCycle');
    }
    return (
      this.workCycles.find((cycle) => cycle.workCycleId === workCycleId)?.name ||
      this.t('restConfigurationsCatalog.labels.noCycle')
    );
  }

  getCompanyName(companiesRecordId: string | null | undefined): string {
    if (!companiesRecordId) {
      return '-';
    }
    return (
      this.companies.find((company) => company.companiesRecordId === companiesRecordId)?.name ||
      '-'
    );
  }

  private async loadConfigurations(
    preferredId: string | null = this.selectedConfigurationId,
    navigateSelection = true
  ): Promise<void> {
    if (!this.selectedCompanyId && this.companies.length === 1) {
      this.selectedCompanyId = this.companies[0].companiesRecordId;
    }

    this.isLoading = true;
    try {
      const params: Record<string, string> = {};
      if (this.selectedCompanyId) {
        params['companiesRecordId'] = this.selectedCompanyId;
      }

      const response = await firstValueFrom(
        this.apiService.get<RestConfigurationConfig[]>('labor-config/rest-configurations', { params })
      );

      this.configurations = (response || []).map((item) => this.normalizeConfiguration(item));
      const nextItem =
        this.configurations.find((item) => item.restConfigurationId === preferredId) ||
        this.configurations[0] ||
        null;

      if (nextItem) {
        await this.selectConfiguration(nextItem.restConfigurationId, false, navigateSelection);
      } else if (preferredId) {
        await this.selectConfiguration(preferredId, false, navigateSelection);
      } else {
        this.selectedConfigurationId = null;
        this.selectedConfiguration = null;
        this.mode = 'view';
      }
    } catch (error) {
      console.error('Error loading rest configurations', error);
      this.configurations = [];
      this.selectedConfigurationId = null;
      this.selectedConfiguration = null;
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('restConfigurationsCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private async selectConfiguration(
    restConfigurationId: string,
    showLoader = true,
    navigate = true
  ): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }

    if (showLoader) {
      this.isLoading = true;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<RestConfigurationConfig>(
          `labor-config/rest-configurations/${restConfigurationId}`
        )
      );
      const normalized = this.normalizeConfiguration(response);
      this.selectedConfigurationId = normalized.restConfigurationId;
      this.selectedConfiguration = normalized;
      this.selectedCompanyId = normalized.companiesRecordId || this.selectedCompanyId;
      this.mode = 'view';
      this.form = this.createFormFromConfiguration(normalized);
      this.reconcileCycleReference();

      if (navigate) {
        void this.router.navigate(['/attendance/rest-configurations', normalized.restConfigurationId], {
          queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
        });
      }
    } catch (error) {
      console.error('Error loading rest configuration detail', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(
          error,
          this.t('restConfigurationsCatalog.notifications.loadDetailError')
        ),
        'bottom',
        'center'
      );
    } finally {
      if (showLoader) {
        this.isLoading = false;
      }
    }
  }

  private async syncWithRoute(): Promise<void> {
    const restConfigurationId = this.route.snapshot.paramMap.get('restConfigurationId');
    const companyId = this.route.snapshot.queryParamMap.get('companyId') || '';
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/rest-configurations/new');
    const isEditRoute = /\/attendance\/rest-configurations\/[^/]+\/edit$/.test(currentUrl);

    if (
      companyId &&
      this.companies.some((company) => company.companiesRecordId === companyId) &&
      companyId !== this.selectedCompanyId
    ) {
      this.selectedCompanyId = companyId;
    }

    await this.loadConfigurations(restConfigurationId, false);

    if (isCreateRoute) {
      this.enterCreateMode(false);
      return;
    }

    if (restConfigurationId && isEditRoute) {
      this.enterEditMode(false);
    }
  }

  private enterCreateMode(navigate = false): void {
    if (!this.canCreate) {
      return;
    }

    this.mode = 'create';
    this.selectedConfigurationId = null;
    this.selectedConfiguration = null;
    this.selectedCompanyId = this.getDefaultCompanyId();
    this.form = this.createEmptyForm();

    if (navigate) {
      void this.router.navigate(['/attendance/rest-configurations/new'], {
        queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
      });
    }
  }

  private enterEditMode(navigate = false): void {
    if (!this.selectedConfiguration || !this.canUpdate) {
      return;
    }

    this.mode = 'edit';
    this.form = this.createFormFromConfiguration(this.selectedConfiguration);
    this.reconcileCycleReference();

    if (navigate && this.selectedConfigurationId) {
      void this.router.navigate(
        ['/attendance/rest-configurations', this.selectedConfigurationId, 'edit'],
        {
          queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
        }
      );
    }
  }

  private exitEditMode(navigate = false): void {
    if (this.selectedConfiguration) {
      this.mode = 'view';
      this.form = this.createFormFromConfiguration(this.selectedConfiguration);
      this.reconcileCycleReference();
      if (navigate && this.selectedConfigurationId) {
        void this.router.navigate(['/attendance/rest-configurations', this.selectedConfigurationId], {
          queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
        });
      }
      return;
    }

    if (this.configurations.length) {
      const firstId = this.configurations[0].restConfigurationId;
      if (navigate) {
        void this.router.navigate(['/attendance/rest-configurations', firstId]);
      } else {
        void this.selectConfiguration(firstId, false, false);
      }
      return;
    }

    this.mode = 'view';
    this.form = this.createEmptyForm();

    if (navigate) {
      void this.router.navigate(['/attendance/rest-configurations']);
    }
  }

  private createEmptyForm(): RestConfigurationFormValue {
    return {
      companiesRecordId: this.getDefaultCompanyId(),
      name: '',
      mode: 'fixed_weekday',
      restDays: [],
      weekdays: [],
      cycleReference: '',
      isActive: true,
    };
  }

  private createFormFromConfiguration(
    item: RestConfigurationConfig
  ): RestConfigurationFormValue {
    return {
      companiesRecordId: item.companiesRecordId || this.selectedCompanyId,
      name: item.name,
      mode: item.mode || 'fixed_weekday',
      restDays: [...item.restDays],
      weekdays: [...item.weekdays],
      cycleReference: item.cycleReference || '',
      isActive: item.isActive !== false,
    };
  }

  private validateForm(): string | null {
    if (!this.form.companiesRecordId) {
      return this.t('orgUnitsCatalog.validationCompany');
    }

    if (!this.normalizeOptionalText(this.form.name)) {
      return this.t('restConfigurationsCatalog.notifications.validationName');
    }

    if (!this.normalizeOptionalText(this.form.mode)) {
      return this.t('restConfigurationsCatalog.notifications.validationMode');
    }

    if (this.isFixedWeekdayMode && !this.form.weekdays.length) {
      return this.t('restConfigurationsCatalog.notifications.validationWeekdays');
    }

    if (this.isCycleReferenceMode) {
      if (!this.form.cycleReference) {
        return this.t('restConfigurationsCatalog.notifications.validationCycleReference');
      }

      if (!this.form.restDays.length) {
        return this.t('restConfigurationsCatalog.notifications.validationRestDays');
      }
    }

    return null;
  }

  private buildCreatePayload(): RestConfigurationCreatePayload {
    const payload: RestConfigurationCreatePayload = {
      companiesRecordId: this.form.companiesRecordId,
      name: this.normalizeOptionalText(this.form.name) || '',
      mode: this.normalizeOptionalText(this.form.mode) || 'fixed_weekday',
      restDays: this.isCycleReferenceMode ? [...this.form.restDays].sort((a, b) => a - b) : [],
      weekdays: this.isFixedWeekdayMode ? [...this.form.weekdays].sort((a, b) => a - b) : [],
      cycleReference: this.isCycleReferenceMode ? this.form.cycleReference.trim() || null : null,
    };

    return payload;
  }

  private buildUpdatePayload(): RestConfigurationUpdatePayload {
    return {
      ...this.buildCreatePayload(),
      isActive: this.form.isActive,
    };
  }

  private normalizeConfiguration(
    item: Partial<RestConfigurationConfig>
  ): RestConfigurationConfig {
    const mode = typeof item.mode === 'string' && item.mode.trim() ? item.mode.trim() : 'fixed_weekday';
    const restDays = Array.isArray(item.restDays) ? item.restDays.map((value) => Number(value)) : [];
    const weekdays = Array.isArray(item.weekdays) ? item.weekdays.map((value) => Number(value)) : [];
    const cycleReference = typeof item.cycleReference === 'string' && item.cycleReference.trim()
      ? item.cycleReference.trim()
      : null;
    const isActive = item.isActive !== false;

    return {
      restConfigurationId: item.restConfigurationId || crypto.randomUUID(),
      center: item.center || '',
      companiesRecordId: item.companiesRecordId,
      name: item.name || '',
      mode,
      restDays,
      weekdays,
      cycleReference,
      isActive,
      companyName: this.getCompanyName(item.companiesRecordId),
      modeLabel: this.getModeLabel(mode),
      summaryLabel:
        mode === 'fixed_weekday'
          ? this.getWeekdaysLabel(weekdays)
          : this.t('restConfigurationsCatalog.labels.cycleDaysSummary', {
              days: this.getRestDaysLabel(restDays),
              cycle: this.getCycleName(cycleReference),
            }),
      cycleReferenceLabel: this.getCycleName(cycleReference),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private async loadCompanies(preserveSelection = true): Promise<void> {
    this.isLoading = true;
    try {
      const response = await this.companyDirectory.getAllActive();

      this.companies = this.normalizeCompanies(response)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));

      if (
        !preserveSelection ||
        !this.selectedCompanyId ||
        !this.companies.some((company) => company.companiesRecordId === this.selectedCompanyId)
      ) {
        this.selectedCompanyId = this.companies[0]?.companiesRecordId || '';
      }
    } catch (error) {
      console.error('Error loading companies for rest configurations', error);
      this.companies = [];
      this.selectedCompanyId = '';
    }
  }

  private async loadWorkCycles(preserveSelection = true): Promise<void> {
    try {
      this.workCycles = (await firstValueFrom(
        this.apiService.get<WorkCycleConfig[]>('labor-config/work-cycles')
      )) || [];

      if (!preserveSelection) {
        this.reconcileCycleReference();
      }
    } catch (error) {
      console.error('Error loading work cycles for rest configurations', error);
      this.workCycles = [];
      this.form.cycleReference = '';
      this.form.restDays = [];
    }
  }

  reconcileCycleReference(): void {
    if (!this.isCycleReferenceMode) {
      return;
    }

    const cycleExists = this.availableWorkCycles.some(
      (cycle) => cycle.workCycleId === this.form.cycleReference
    );

    if (!cycleExists) {
      this.form.cycleReference = '';
      this.form.restDays = [];
      return;
    }

    const allowedDays = new Set(this.availableRestDayOptions);
    this.form.restDays = this.form.restDays.filter((day) => allowedDays.has(day));
  }

  private getDefaultCompanyId(): string {
    if (this.companies.length === 1) {
      return this.selectedCompanyId || this.companies[0]?.companiesRecordId || '';
    }
    return this.selectedCompanyId || '';
  }

  private normalizeCompanies(
    response: CompaniesRecord[] | CompaniesRecordsResponse | null | undefined
  ): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response?.items || response?.data || response?.results || response?.companiesRecords || [];
  }

  private normalizeOptionalText(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    const normalizedValue = value.trim();
    return normalizedValue || undefined;
  }

  private getApiErrorMessage(error: unknown, fallback: string): string {
    return extractApiErrorMessage(error, fallback);
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'restConfigurations', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
